/* eslint-disable max-lines */
import type { ConnectedPosition, Overlay } from '@angular/cdk/overlay';
import { DestroyRef, type ElementRef, inject, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';

import { switchMap } from 'rxjs/operators';

import { Ability } from '@core/abilities-types';
import { injectHasNewDashboard } from '@core/dashboard-util';
import { GettingStartedFacade } from '@core/getting-started-legacy';
import { GettingStartedSubtask } from '@core/getting-started-types';
import { I18nService } from '@core/i18n-legacy';
import { ConnectedOverlayDirective } from '@core/user-interface-legacy';
import {
  flexiblePositionStrategyFactory,
  type Step,
  WalkthroughFacadeService,
  WalkthroughNavigationHelperService,
  WalkthroughStepsService,
  WalkthroughTooltipComponent,
} from '@core/walkthrough-legacy';

@Injectable({ providedIn: 'root' })
export class NavigationWalkthroughService {
  private readonly destroyRef = inject(DestroyRef);
  private readonly hasNewDashboard = injectHasNewDashboard();
  public readonly subtask = GettingStartedSubtask.NavigationTour;
  private dropdownRef: ConnectedOverlayDirective;

  private steps: Step[] = [
    {
      id: 'walkthrough-step-navigation-1',
      position: 1,
      component: {
        definition: WalkthroughTooltipComponent,
        inputs: {
          arrowConfig: ['middle', 'left'],
          heading: this.i18n.translate('app.walkthrough.walkthroughs.navigation.step_1.heading'),
          description: this.i18n.translate(
            'app.walkthrough.walkthroughs.navigation.step_1.description',
          ),
          ctaText: this.i18n.translate('app.onboarding.walkthrough_tooltip.next_step'),
          ctaIcon: 'chevron-right',
        },
      },
      renderOptions: {
        strategy: flexiblePositionStrategyFactory,
        useWalkthroughBackdropClass: true,
        positions: [{ originX: 'end', originY: 'center', overlayX: 'start', overlayY: 'center' }],
        offset: { offsetX: 36, offsetY: 0 },
      },
    },
    {
      id: 'walkthrough-step-navigation-2',
      position: 2,
      component: {
        definition: WalkthroughTooltipComponent,
        inputs: {
          arrowConfig: ['middle', 'left'],
          heading: this.i18n.translate('app.walkthrough.walkthroughs.navigation.step_2.heading'),
          description: this.i18n.translate(
            'app.walkthrough.walkthroughs.navigation.step_2.description',
          ),
          ctaText: this.i18n.translate('app.onboarding.walkthrough_tooltip.next_step'),
          ctaIcon: 'chevron-right',
        },
      },
      renderOptions: {
        strategy: flexiblePositionStrategyFactory,
        useWalkthroughBackdropClass: true,
        positions: [{ originX: 'end', originY: 'center', overlayX: 'start', overlayY: 'center' }],
        offset: { offsetX: 36, offsetY: 0 },
      },
    },
    // {
    //   id: 'walkthrough-step-navigation-3',
    //   position: 3,
    //   component: {
    //     definition: WalkthroughTooltipComponent,
    //     inputs: {
    //       arrowConfig: ['middle', 'left'],
    //       heading: this.i18n.translate('app.walkthrough.walkthroughs.navigation.step_3.heading'),
    //       description: this.i18n.translate(
    //         'app.walkthrough.walkthroughs.navigation.step_3.description',
    //       ),
    //       ctaText: this.i18n.translate('app.onboarding.walkthrough_tooltip.next_step'),
    //       ctaIcon: 'chevron-right',
    //     },
    //   },
    //   renderOptions: {
    //     strategy: appSwitcherCareersSiteItemPositionStrategyFactory,
    //     useWalkthroughBackdropClass: true,
    //     positions: [{ originX: 'end', originY: 'center', overlayX: 'start', overlayY: 'center' }],
    //     offset: { offsetX: 36, offsetY: -50 },
    //   },
    //   features: [WalkthroughFeatureFilter.CareersSite],
    // },
    {
      id: 'walkthrough-step-navigation-4',
      position: 3,
      component: {
        definition: WalkthroughTooltipComponent,
        inputs: {
          arrowConfig: ['right', 'top'],
          heading: this.i18n.translate('app.walkthrough.walkthroughs.navigation.step_4.heading'),
          description: this.i18n.translate(
            'app.walkthrough.walkthroughs.navigation.step_4.description',
          ),
          ctaText: this.i18n.translate('app.onboarding.walkthrough_tooltip.next_step'),
          ctaIcon: 'chevron-right',
        },
      },
      renderOptions: {
        strategy: flexiblePositionStrategyFactory,
        useWalkthroughBackdropClass: true,
        positions: [{ originX: 'center', originY: 'bottom', overlayX: 'end', overlayY: 'top' }],
        offset: { offsetX: 40, offsetY: 24 },
      },
    },
    {
      id: 'walkthrough-step-navigation-5',
      position: 4,
      component: {
        definition: WalkthroughTooltipComponent,
        inputs: {
          arrowConfig: ['right', 'top'],
          heading: this.i18n.translate('app.walkthrough.walkthroughs.navigation.step_5.heading'),
          description: this.i18n.translate(
            'app.walkthrough.walkthroughs.navigation.step_5.description',
          ),
          ctaText: this.i18n.translate('app.onboarding.walkthrough_tooltip.next_step'),
          ctaIcon: 'chevron-right',
        },
      },
      renderOptions: {
        strategy: flexiblePositionStrategyFactory,
        useWalkthroughBackdropClass: true,
        positions: [{ originX: 'center', originY: 'bottom', overlayX: 'end', overlayY: 'top' }],
        offset: { offsetX: 40, offsetY: 24 },
      },
    },
    {
      id: 'walkthrough-step-navigation-7',
      position: 5,
      component: {
        definition: WalkthroughTooltipComponent,
        inputs: {
          arrowConfig: ['middle', 'top'],
          heading: this.i18n.translate('app.walkthrough.walkthroughs.navigation.step_7.heading'),
          description: this.i18n.translate(
            'app.walkthrough.walkthroughs.navigation.step_7.description',
          ),
          ctaText: this.i18n.translate('app.onboarding.walkthrough_tooltip.next_step'),
          ctaIcon: 'chevron-right',
        },
      },
      renderOptions: {
        strategy: flexiblePositionStrategyFactory,
        useWalkthroughBackdropClass: true,
        positions: [{ originX: 'center', originY: 'bottom', overlayX: 'center', overlayY: 'top' }],
        offset: { offsetX: -108, offsetY: 245 },
      },
      abilities: [Ability.ManageRecruiters, Ability.ManageJobs, Ability.ManageCandidates],
    },
    {
      id: 'walkthrough-step-navigation-6',
      position: 6,
      component: {
        definition: WalkthroughTooltipComponent,
        inputs: {
          arrowConfig: ['middle', 'top'],
          heading: this.i18n.translate('app.walkthrough.walkthroughs.navigation.step_6.heading'),
          description: this.i18n.translate(
            'app.walkthrough.walkthroughs.navigation.step_6.description',
          ),
          ctaText: this.i18n.translate('app.onboarding.walkthrough_tooltip.next_step'),
          ctaIcon: 'chevron-right',
        },
      },
      renderOptions: {
        strategy: flexiblePositionStrategyFactory,
        useWalkthroughBackdropClass: true,
        positions: [{ originX: 'center', originY: 'bottom', overlayX: 'center', overlayY: 'top' }],
        offset: { offsetX: 0, offsetY: 24 },
      },
      abilities: [Ability.ViewCandidates, Ability.ViewJobs],
    },
    {
      id: 'walkthrough-step-navigation-8',
      position: 7,
      component: {
        definition: WalkthroughTooltipComponent,
        inputs: {
          arrowConfig: this.hasNewDashboard ? ['left', 'top'] : ['middle', 'left'],
          heading: this.i18n.translate(
            this.hasNewDashboard
              ? 'app.walkthrough.walkthroughs.navigation.step_8_v2.heading'
              : 'app.walkthrough.walkthroughs.navigation.step_8.heading',
          ),
          description: this.i18n.translate(
            this.hasNewDashboard
              ? 'app.walkthrough.walkthroughs.navigation.step_8_v2.description'
              : 'app.walkthrough.walkthroughs.navigation.step_8.description',
          ),
          ctaText: this.i18n.translate('app.onboarding.walkthrough_tooltip.next_step'),
          ctaIcon: 'chevron-right',
        },
      },
      renderOptions: {
        strategy: flexiblePositionStrategyFactory,
        useWalkthroughBackdropClass: true,
        positions: [
          this.hasNewDashboard
            ? { originX: 'center', originY: 'bottom', overlayX: 'center', overlayY: 'top' }
            : { originX: 'end', originY: 'center', overlayX: 'start', overlayY: 'center' },
        ],
        offset: this.hasNewDashboard ? { offsetX: 124, offsetY: 24 } : { offsetX: 24, offsetY: 20 },
      },
      abilities: [Ability.ViewCandidates, Ability.ViewCalendar],
    },
    {
      id: 'walkthrough-step-navigation-9',
      position: 8,
      component: {
        definition: WalkthroughTooltipComponent,
        inputs: {
          arrowConfig: this.hasNewDashboard ? ['middle', 'top'] : ['middle', 'left'],
          heading: this.i18n.translate(
            this.hasNewDashboard
              ? 'app.walkthrough.walkthroughs.navigation.step_9_v2.heading'
              : 'app.walkthrough.walkthroughs.navigation.step_9.heading',
          ),
          description: this.i18n.translate(
            this.hasNewDashboard
              ? 'app.walkthrough.walkthroughs.navigation.step_9_v2.description'
              : 'app.walkthrough.walkthroughs.navigation.step_9.description',
          ),
          ctaText: this.i18n.translate('app.onboarding.walkthrough_tooltip.next_step'),
          ctaIcon: 'chevron-right',
        },
      },
      renderOptions: {
        strategy: flexiblePositionStrategyFactory,
        useWalkthroughBackdropClass: true,
        positions: [
          this.hasNewDashboard
            ? { originX: 'center', originY: 'bottom', overlayX: 'center', overlayY: 'top' }
            : { originX: 'end', originY: 'center', overlayX: 'start', overlayY: 'center' },
        ],
        offset: this.hasNewDashboard ? { offsetX: -42, offsetY: 24 } : { offsetX: 24, offsetY: 20 },
      },
      abilities: [Ability.ViewJobs],
    },
    {
      id: 'walkthrough-step-navigation-10',
      position: 9,
      component: {
        definition: WalkthroughTooltipComponent,
        inputs: {
          arrowConfig: this.hasNewDashboard ? ['middle', 'top'] : ['middle', 'left'],
          heading: this.i18n.translate(
            this.hasNewDashboard
              ? 'app.walkthrough.walkthroughs.navigation.step_10_v2.heading'
              : 'app.walkthrough.walkthroughs.navigation.step_10.heading',
          ),
          description: this.i18n.translate(
            this.hasNewDashboard
              ? 'app.walkthrough.walkthroughs.navigation.step_10_v2.description'
              : 'app.walkthrough.walkthroughs.navigation.step_10.description',
          ),
          ctaText: this.i18n.translate('app.onboarding.walkthrough_tooltip.got_it'),
        },
      },
      renderOptions: {
        strategy: flexiblePositionStrategyFactory,
        useWalkthroughBackdropClass: true,
        positions: [
          this.hasNewDashboard
            ? { originX: 'center', originY: 'bottom', overlayX: 'center', overlayY: 'top' }
            : { originX: 'end', originY: 'center', overlayX: 'start', overlayY: 'center' },
        ],
        offset: this.hasNewDashboard ? { offsetX: -8, offsetY: 24 } : { offsetX: 24, offsetY: 0 },
      },
      finishHandler: () => {
        this.finishHandler();
      },
    },
  ];

  constructor(
    private walkthroughStepsService: WalkthroughStepsService,
    private router: Router,
    private i18n: I18nService,
    private gettingStartedFacade: GettingStartedFacade,
    private walkthroughFacadeService: WalkthroughFacadeService,
    private navigationHelperService: WalkthroughNavigationHelperService,
  ) {}

  public start(): void {
    this.walkthroughFacadeService
      .getFilteredStepsByAbilities(this.steps)
      .pipe(
        switchMap(steps => this.walkthroughFacadeService.filterByCareersSiteFeature(steps)),
        switchMap(steps => this.walkthroughStepsService.start(steps)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(state => {
        this.navigationHelperService.handleWalkthroughState(state, this.subtask, [
          'dashboard',
          'overview',
        ]);
      });

    this.walkthroughStepsService.onLastStep$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.gettingStartedFacade.markSubtaskAsDone(this.subtask);
      });
  }

  public navigateToFirstStep(): void {
    this.router.navigate(['dashboard', 'overview']);
  }

  private finishHandler(): void {
    this.navigationHelperService.navigateAndOpenDrawer(['dashboard', 'overview']);
  }
}

export function appSwitcherCareersSiteItemPositionStrategyFactory(
  overlay: Overlay,
  origin: ElementRef,
  positions: ConnectedPosition[],
  offset: { offsetX: number; offsetY: number },
) {
  const result = overlay
    .position()
    .flexibleConnectedTo(origin)
    .withPositions(positions)
    .withDefaultOffsetX(offset.offsetX)
    .withDefaultOffsetY(offset.offsetY)
    .withPush(true);

  // query the element within the opened dropdown, set it as origin
  setTimeout(() => {
    const el: HTMLElement = document.querySelector('.item.item--careers')!;

    if (el) {
      result.setOrigin(el);
      result.apply();
    }
  }, 10);

  return result;
}
